import { computed, unref } from 'vue';
import { useType as useOrientation } from '~/modules/type/composables/useType';
import { useTraits } from '~/composables/useTraits';
import useConsolidatedLatchkey from '~/modules/latchkey/composables/useConsolidatedLatchkey';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductType } from '~/composables/useProductType';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import { getMemberProduct } from '~/utils/product';

import type { Image } from '~~/shared/globalTypes/product';
import type { SelectedOrientationId } from '~/declaration/traits';

export const PLACEHOLDER_IMAGE: Image = {
  filePath: '',
  name: '',
  showJumboSwatch: false,
  swatchSpriteOffset: 0,
  swatchSpriteUrlIndex: 0,
};

function swapFirst2Images(images: Image[]): Image[] {
  if (images.length < 2) {
    return images;
  }
  const [img1, img2, ...restOfImages] = images;
  return [img2, img1, ...restOfImages] as Image[];
}

function isRightArmOrientation(selectedOrientationId: SelectedOrientationId) {
  return unref(selectedOrientationId) === 1;
}

function setImageOrderByOrientation(images: Image[], isBigTicketItemFlag: boolean, selectedTypeId: SelectedOrientationId): Image[] {
  if (isBigTicketItemFlag && isRightArmOrientation(selectedTypeId)) {
    return swapFirst2Images(images);
  }
  return images;
}

function get3vMemberImage(product: Product, selectedColorId: ColorId | undefined, selectedProductIdStr: ProductId): Image[] | null {
  if (!product?.detail?.flags?.isConsolidatedThirdVariant || !selectedColorId || !selectedProductIdStr) return null;

  return getMemberProduct(unref(product), selectedProductIdStr)?.traits?.colors?.colorMap?.[selectedColorId]?.imagery?.images || null;
}

export default function useRawImages(): ComputedRef<Image[]> {
  const { productData: product } = useProduct();
  const selectedProductId = useSelectedMemberId();
  const { relationships } = product;
  const { isConsolidatedLatchKey, isConsolidatedStandard, isConsolidatedMaster } = useProductType();
  const { isBigTicketItemFlag } = useProductFlags();
  const { selectedTypeId } = useOrientation();

  if (isConsolidatedLatchKey) {
    return useConsolidatedLatchkey().images;
  }

  const { selectedColor } = useTraits();

  if (isConsolidatedStandard || isConsolidatedMaster) {
    const { selectedUpcId } = useUpc();

    return computed(
      () =>
        (selectedUpcId.value && relationships?.upcs?.[unref(selectedUpcId)]?.imagery?.images) ||
        get3vMemberImage(product, selectedColor.value?.id, selectedProductId.value) ||
        selectedColor.value?.imagery?.images ||
        product?.imagery?.images || [PLACEHOLDER_IMAGE],
    );
  }

  return computed(() => {
    const images = selectedColor.value?.imagery?.images || product?.imagery?.images || [PLACEHOLDER_IMAGE];
    return setImageOrderByOrientation(images, isBigTicketItemFlag, selectedTypeId);
  });
}
